import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Heading,
  Divider,
  SimpleGrid,
  IconButton,
  Textarea,
  useColorModeValue,
  List,
  ListItem,
  InputGroup,
  InputRightElement,
  Text,
  Select,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import { ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';
import { getData, postData } from '../../utils/httpClient.js';
import PageTable from 'components/layout/PageTable';
import { calculateItemTotals, parseDecimalValue } from '../../utils/invoiceCalculations';
import useBcuUsdRate from '../../hooks/useBcuUsdRate';
import { formatMoneyDual, formatMoneyCode } from '../../utils/currencyDisplay';
import { formatNumber } from '../../utils/transform';

export default function CreatePurchaseInvoice({ onCreated }) {
  const [form, setForm] = useState({
    proveedor_id: '',
    numero_factura: '',
    fecha_factura: '',
    fecha_vencimiento: '',
    moneda: 'UYU',
    subtotal: 0,
    iva: 0,
    total: 0,
    estado: 'Pendiente',
    descripcion: '',
    items: [],
  });

  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showManualProductForm, setShowManualProductForm] = useState(false);
  const toast = useToast();
  const { rate: usdRate } = useBcuUsdRate(
    form.moneda === 'USD' ? form.fecha_factura || undefined : undefined,
  );

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadProviders();
    loadProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProviders = async () => {
    try {
      const response = await getData('/providers');
      setProviders(response?.payload?.providers || []);
    } catch (error) {
      toast({
        title: 'Error al cargar proveedores',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getData('/products?page=1&limit=500');
      console.log('Productos cargados:', response?.payload?.products);
      setProducts(response?.payload?.products || []);
    } catch (error) {
      toast({
        title: 'Error al cargar productos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleProviderChange = (providerId) => {
    const selectedProvider = providers.find(p => p.id === parseInt(providerId));
    setForm(prev => ({
      ...prev,
      proveedor_id: providerId,
      proveedor: selectedProvider
    }));
  };

  const filterProducts = (value) => {
    const term = value.trim().toLowerCase();
    if (!term) {
      return products.slice(0, 20);
    }
    return products.filter(
      (product) =>
        product.name?.toLowerCase().includes(term) ||
        product.code?.toLowerCase().includes(term),
    );
  };

  const handleProductSearch = (value) => {
    setProductSearch(value);
    const filtered = filterProducts(value);
    setFilteredProducts(filtered);
    setShowProductSuggestions(filtered.length > 0);
  };

  // Funciones para productos manuales
  const addManualProduct = () => {
    setShowManualProductForm(true);
  };

  const addManualProductToForm = async (productData) => {
    try {
      const res = await postData('/products', {
        code: productData.code || `PC-${Date.now()}`,
        name: productData.name,
        currentCost: parseDecimalValue(productData.currentCost),
        currentStock: 0,
        categoryId: null,
        unitId: null,
        currencyId: 1,
        providerId: form.proveedor_id ? Number(form.proveedor_id) : null,
      });
      const created = res?.payload?.product;
      if (created) {
        addProduct({ ...created, type: 'manual' });
        setProducts((prev) => [...prev, created]);
      }
      setShowManualProductForm(false);
      setProductSearch('');
      toast({
        title: 'Producto creado y agregado',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error al crear producto',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const addProduct = (product) => {
    const newItem = {
      id: Date.now(),
      producto_id: product.id,
      codigo: product.code || 'S/N',
      descripcion: product.name,
      cantidad: 1,
      cantidad_input: '1',
      precio: product.currentCost || 0,
      precio_input: String(product.currentCost || 0),
      subtotal: 0,
      iva_porcentaje: 22,
      iva_monto: 0,
      total: 0,
      ivaIncluded: Boolean(product.ivaIncluded),
      isManual: product.type === 'manual',
    };
    calculateItemTotals(newItem);

    setForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setProductSearch('');
    setShowProductSuggestions(false);
  };

  const calculateItemTotalsLocal = (item) => calculateItemTotals(item);

  const updateItem = (itemId, field, value) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item };
          
          if (field === 'ivaIncluded') {
            updatedItem[field] = value;
          } else if (field === 'cantidad' || field === 'precio') {
            updatedItem[`${field}_input`] = value;
            updatedItem[field] = parseDecimalValue(value);
          } else {
            updatedItem[field] = parseDecimalValue(value);
          }

          if (field === 'cantidad' || field === 'precio' || field === 'ivaIncluded') {
            calculateItemTotalsLocal(updatedItem);
          }

          return updatedItem;
        }
        return item;
      })
    }));
  };


  const removeItem = (itemId) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const calculateTotals = () => {
    const subtotal = form.items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const iva = form.items.reduce((sum, item) => sum + (item.iva_monto || 0), 0);
    const total = subtotal + iva;

    setForm(prev => ({
      ...prev,
      subtotal,
      iva,
      total
    }));
  };

  useEffect(() => {
    calculateTotals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items]);

  const handleSubmit = async () => {
    if (!form.proveedor_id || !form.numero_factura || form.items.length === 0) {
      toast({
        title: 'Campos requeridos',
        description: 'Complete proveedor, número de factura y al menos un producto',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const invalidQty = form.items.some((i) => !i.cantidad || i.cantidad <= 0);
    if (invalidQty) {
      toast({
        title: 'Cantidad inválida',
        description: 'Todos los productos deben tener cantidad mayor a 0',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const currencyId = form.moneda === 'USD' ? 2 : 1;
      const payload = {
        invoiceId: form.numero_factura,
        providerId: form.proveedor_id,
        date: form.fecha_factura,
        expirationDate: form.fecha_vencimiento,
        currencyId,
        status: form.estado,
        type: 'Compra',
        totalAmount: form.total,
        providerInvoiceDetails: form.items.map(item => ({
          productId: item.isManual ? null : item.producto_id,
          productName: item.descripcion,
          productCode: item.codigo,
          quantity: item.cantidad,
          cost: item.precio,
          currencyId,
          totalWoTax: item.subtotal,
          taxPercentage: item.iva_porcentaje,
          taxAmount: item.iva_monto,
          totalWTax: item.total,
        }))
      };

      console.log('Payload factura de compra:', payload);

      await postData('/provider-invoices', payload);

      toast({
        title: 'Factura de compra registrada',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onCreated?.();

      // Reset form
      setForm({
        proveedor_id: '',
        numero_factura: '',
        fecha_factura: '',
        fecha_vencimiento: '',
        moneda: 'UYU',
        subtotal: 0,
        iva: 0,
        total: 0,
        estado: 'Pendiente',
        descripcion: '',
        items: [],
      });

    } catch (error) {
      toast({
        title: 'Error al registrar factura de compra',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };


  // Componente para el formulario de producto manual
const ManualProductForm = ({ onAdd, onCancel }) => {
    const [manualProduct, setManualProduct] = useState({
      code: '',
      name: '',
      unit: 'un',
      currentCost: '',
    });

    const handleManualProductChange = (field, value) => {
      setManualProduct((prev) => ({ ...prev, [field]: value }));
    };

    const handleAddManualProduct = () => {
      if (!manualProduct.name.trim() || !manualProduct.currentCost) {
        toast({
          title: 'Error',
          description: 'Nombre y precio son obligatorios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Usar parseDecimalValue para manejar comas en el precio
      const productWithParsedPrice = {
        ...manualProduct,
        currentCost: parseDecimalValue(manualProduct.currentCost)
      };

      onAdd(productWithParsedPrice);
      setManualProduct({ code: '', name: '', unit: 'un', currentCost: '' });
    };

    return (
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
        <FormControl>
          <FormLabel fontSize="sm">Código</FormLabel>
          <Input
            size="sm"
            value={manualProduct.code}
            onChange={(e) => handleManualProductChange('code', e.target.value)}
            placeholder="Código del producto"
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre del producto</FormLabel>
          <Input
            size="sm"
            value={manualProduct.name}
            onChange={(e) => handleManualProductChange('name', e.target.value)}
            placeholder="Ej: Filtro de aceite"
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Unidad</FormLabel>
          <Select
            size="sm"
            value={manualProduct.unit}
            onChange={(e) => handleManualProductChange('unit', e.target.value)}
          >
            <option value="Unidad">UNIDAD</option>
            <option value="kg">KILO</option>
            <option value="lt">LITRO</option>
            <option value="m">METRO</option>
            <option value="h">HORA</option>
            <option value="min">MINUTO</option>
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Precio</FormLabel>
          <Input
            size="sm"
            type="text"
            value={manualProduct.currentCost}
            onChange={(e) => handleManualProductChange('currentCost', e.target.value)}
            placeholder="0,00"
          />
        </FormControl>
        <HStack mt={2} gridColumn={{ base: 'span 1', md: 'span 4' }}>
          <Button
            size="sm"
            colorScheme="gray"
            onClick={handleAddManualProduct}
          >
            Agregar
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        </HStack>
      </SimpleGrid>
    );
  };


  return (
    <PageTable flexDirection="column" w="100%" px={{ base: 3, md: 4 }} py={{ base: 3, md: 4 }} stickyActions={false}>
      <VStack spacing={4} align="stretch">
        <Heading size="md" color="secondaryGray.900">
          Registrar Factura de Compra
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <FormControl isRequired>
            <FormLabel fontSize="sm">Proveedor</FormLabel>
            <Select
              size="sm"
              placeholder="Seleccionar proveedor"
              value={form.proveedor_id}
              onChange={(e) => handleProviderChange(e.target.value)}
            >
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} - {provider.rut}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Número de Factura</FormLabel>
            <Input
              value={form.numero_factura}
              onChange={(e) => setForm(prev => ({ ...prev, numero_factura: e.target.value }))}
              placeholder="Ej: A001-00001234"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Fecha de Factura</FormLabel>
            <Input
              type="date"
              value={form.fecha_factura}
              onChange={(e) => setForm(prev => ({ ...prev, fecha_factura: e.target.value }))}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Fecha de Vencimiento</FormLabel>
            <Input
              type="date"
              value={form.fecha_vencimiento}
              onChange={(e) => setForm(prev => ({ ...prev, fecha_vencimiento: e.target.value }))}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Moneda</FormLabel>
            <Select
              value={form.moneda}
              onChange={(e) => setForm(prev => ({ ...prev, moneda: e.target.value }))}
            >
              <option value="UYU">Pesos Uruguayos (UYU)</option>
              <option value="USD">Dólares (USD)</option>
            </Select>
          </FormControl>

          {form.moneda === 'USD' && (
            <FormControl>
              <FormLabel>Tipo de cambio (BCU)</FormLabel>
              <Input
                value={usdRate ? Number(usdRate).toFixed(3) : 'Consultando…'}
                isReadOnly
                bg="gray.50"
              />
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Estado</FormLabel>
            <Select
              value={form.estado}
              onChange={(e) => setForm(prev => ({ ...prev, estado: e.target.value }))}
            >
              <option value="pendiente">Pendiente</option>
              <option value="pagada">Pagada</option>
              <option value="vencida">Vencida</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel>Descripción (Opcional)</FormLabel>
          <Textarea
            value={form.descripcion}
            onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
            placeholder="Descripción adicional de la factura"
          />
        </FormControl>

        <Divider />

        <VStack spacing={4} align="stretch">
          <Heading size="md">Productos</Heading>

          <FormControl>
            <FormLabel>Buscar Producto</FormLabel>
            <HStack spacing={3} align="start">
            <InputGroup>
              <Input
                value={productSearch}
                onChange={(e) => handleProductSearch(e.target.value)}
                onFocus={() => handleProductSearch(productSearch)}
                placeholder="Buscar por código o descripción"
              />
              <InputRightElement>
                <ChevronDownIcon />
              </InputRightElement>
            </InputGroup>
            <Button onClick={addManualProduct} variant="adminPrimary" minW="120px">
              + Manual
            </Button>
            </HStack>

            {showProductSuggestions && filteredProducts.length > 0 && (
              <Box
                position="relative"
                bg={bgColor}
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                mt={1}
                maxH="200px"
                overflowY="auto"
                zIndex={10}
              >
                <List>
                  {filteredProducts.slice(0, 10).map((product) => (
                    <ListItem
                      key={product.id}
                      p={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => addProduct(product)}
                    >
                      <Text fontSize="sm" fontWeight="medium" color="blue.600">
                        {product.code}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {product.name}
                        {product.currentCost != null && (
                          <>
                            {' · '}
                            {formatMoneyDual(
                              product.currentCost,
                              product.currency?.code || 'UYU',
                              usdRate || product.currency?.rate,
                            )}
                          </>
                        )}
                      </Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
          </FormControl>
          {/* Formulario para agregar producto manual */}
          {showManualProductForm && (
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={3} align="stretch">
                <Heading size="sm" color="green.700">
                  Agregar Producto Manual
                </Heading>
                <ManualProductForm
                  onAdd={addManualProductToForm}
                  onCancel={() => setShowManualProductForm(false)}
                />
              </VStack>
            </Box>
          )}

          {form.items.length > 0 && (
            <Box overflowX="auto">
              <SimpleGrid columns={8} spacing={2} minChildWidth="100px" mb={2}>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Código</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Descripción</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Cantidad</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Precio Unit.</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">IVA Inc.</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Subtotal</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Total</Text>
                <Text fontWeight="medium" fontSize="sm" color="secondaryGray.600">Acciones</Text>
              </SimpleGrid>

              {form.items.map((item) => (
                <SimpleGrid key={item.id} columns={8} spacing={2} minChildWidth="100px" mb={2} alignItems="center">
                  <Text fontSize="sm" color="secondaryGray.900">{item.codigo}</Text>
                  <Text fontSize="sm" color="secondaryGray.900" noOfLines={2}>{item.descripcion}</Text>
                  <Input
                    size="sm"
                    type="text"
                    value={item.cantidad_input ?? item.cantidad}
                    onChange={(e) => updateItem(item.id, 'cantidad', e.target.value)}
                    placeholder="1,5"
                  />
                  <Input
                    size="sm"
                    type="text"
                    value={item.precio_input ?? item.precio}
                    onChange={(e) => updateItem(item.id, 'precio', e.target.value)}
                    placeholder="0,00"
                  />
                  <Checkbox
                    size="sm"
                    isChecked={item.ivaIncluded || false}
                    onChange={(e) => updateItem(item.id, 'ivaIncluded', e.target.checked)}
                  />
                  <Text fontSize="sm" color="secondaryGray.900">${item.subtotal?.toFixed(2) || '0.00'}</Text>
                  <Text fontSize="sm" fontWeight="medium" color="green.600">${item.total?.toFixed(2) || '0.00'}</Text>
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => removeItem(item.id)}
                  />
                </SimpleGrid>
              ))}
            </Box>
          )}
        </VStack>

        <Divider />

        <HStack justify="end" spacing={4}>
          <VStack align="end" spacing={2}>
            <Text fontSize="sm" color="secondaryGray.600">
              Subtotal:{' '}
              <Text as="span" fontWeight="medium" color="secondaryGray.900">
                {formatMoneyCode(form.subtotal, form.moneda)}
              </Text>
            </Text>
            <Text fontSize="sm" color="secondaryGray.600">
              IVA:{' '}
              <Text as="span" fontWeight="medium" color="secondaryGray.900">
                {formatMoneyCode(form.iva, form.moneda)}
              </Text>
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="green.600">
              Total: {formatMoneyDual(form.total, form.moneda, usdRate)}
            </Text>
            {form.moneda === 'USD' && usdRate > 0 && (
              <Text fontSize="xs" color="gray.500">
                TC BCU {Number(usdRate).toFixed(3)} · Equiv. UYU{' '}
                {formatNumber(Number(form.total) * usdRate)}
              </Text>
            )}
          </VStack>
        </HStack>

        <HStack justify="end" spacing={4}>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Registrando..."
          >
            Registrar Factura de Compra
          </Button>
        </HStack>
      </VStack>
    </PageTable>
  );
}
